/**
 * dokumentVorlagen — Texte und Optionen aller PDF-Dokumente an EINER Stelle.
 *
 * Quelle der Wahrheit (absteigende Priorität):
 *   1. pdf_settings.vorlagen[typ][feld]   (vom Admin gepflegt, JSONB)
 *   2. alte pdf_settings-Spalten          (greeting_offer, closing_order, …)
 *   3. eingebaute Standardtexte           (= die bisher hart codierten Texte)
 *
 * Platzhalter {{kunde.name}} werden erst beim Rendern ersetzt; unbekannte
 * Platzhalter werden leer. Texte sind immer reiner Text (HTML wird escaped),
 * **fett** ist die einzige Auszeichnung, Zeilenumbruch = <br>.
 */
import { escapeHtml } from './pdfRenderer';

export const DOKUMENT_TYPEN = Object.freeze([
  { id: 'angebot', label: 'Angebot', gruppe: 'Vertrieb' },
  { id: 'bestellung', label: 'Auftragsbestätigung', gruppe: 'Vertrieb' },
  { id: 'lieferschein', label: 'Lieferschein', gruppe: 'Lager' },
  { id: 'kommissionierschein', label: 'Kommissionierschein', gruppe: 'Lager' },
  { id: 'rechnung', label: 'Rechnung', gruppe: 'Buchhaltung' },
  { id: 'gutschrift', label: 'Gutschrift', gruppe: 'Buchhaltung' },
  { id: 'mahnung_0', label: 'Zahlungserinnerung', gruppe: 'Mahnwesen' },
  { id: 'mahnung_1', label: '1. Mahnung', gruppe: 'Mahnwesen' },
  { id: 'mahnung_2', label: '2. Mahnung', gruppe: 'Mahnwesen' },
  { id: 'mahnung_3', label: 'Inkasso-Ankündigung', gruppe: 'Mahnwesen' },
]);

const ANREDE = 'Sehr geehrte Damen und Herren,';
const GRUSS = 'Mit freundlichen Grüßen';

/** Platzhalter-Katalog: key → Beschreibung + Beispiel (für Chips im Editor). */
export const PLATZHALTER = Object.freeze({
  'kunde.name': { label: 'Kundenname', beispiel: 'Muster Restaurant GmbH' },
  'kunde.nummer': { label: 'Kundennummer', beispiel: 'DEMO' },
  'firma.name': { label: 'Firmenname', beispiel: 'InMotion Großhandel UG' },
  'firma.geschaeftsfuehrer': { label: 'Geschäftsführung', beispiel: 'Bünyamin Cicekdag' },
  'firma.ansprechpartner': { label: 'Ansprechpartner', beispiel: 'Buchhaltung' },
  'beleg.nummer': { label: 'Belegnummer', beispiel: 'ANG-2026-0001' },
  'beleg.datum': { label: 'Belegdatum', beispiel: '03.11.2026' },
  'angebot.gueltig_bis': { label: 'Angebot gültig bis', beispiel: '03.12.2026' },
  'bestellung.nummer': { label: 'Bestellnummer', beispiel: 'BE-2026-0001' },
  'bestellung.mindestbestellwert': { label: 'Mindestbestellwert', beispiel: '285,00' },
  'rechnung.nummer': { label: 'Rechnungsnummer', beispiel: 'RE-2026-0001' },
  'rechnung.datum': { label: 'Rechnungsdatum', beispiel: '03.11.2026' },
  'rechnung.zahlungsziel_tage': { label: 'Zahlungsziel (Tage)', beispiel: '14' },
  'mahnung.frist_datum': { label: 'Zahlbar bis', beispiel: '10.11.2026' },
  'mahnung.frist_tage': { label: 'Frist (Tage)', beispiel: '7' },
  'mahnung.gebuehr_prozent': { label: 'Mahngebühr %', beispiel: '8' },
});

const gemeinsam = ['kunde.name', 'kunde.nummer', 'firma.name', 'firma.geschaeftsfuehrer', 'firma.ansprechpartner', 'beleg.nummer', 'beleg.datum'];
const PLATZHALTER_JE_TYP = {
  angebot: [...gemeinsam, 'angebot.gueltig_bis'],
  bestellung: [...gemeinsam, 'bestellung.nummer', 'bestellung.mindestbestellwert'],
  lieferschein: [...gemeinsam, 'bestellung.nummer'],
  kommissionierschein: [...gemeinsam, 'bestellung.nummer'],
  rechnung: [...gemeinsam, 'rechnung.nummer', 'rechnung.datum', 'rechnung.zahlungsziel_tage', 'bestellung.nummer'],
  gutschrift: [...gemeinsam, 'rechnung.nummer'],
  mahnung: [...gemeinsam, 'rechnung.nummer', 'rechnung.datum', 'mahnung.frist_datum', 'mahnung.frist_tage', 'mahnung.gebuehr_prozent'],
};

const f = (key, label, standard, opts = {}) => ({ key, label, standard, typ: opts.typ || 'textarea', hilfe: opts.hilfe || '', legacy: opts.legacy || null });

const MAHNUNG_TEXTE = {
  0: 'sicherlich ist es Ihrer Aufmerksamkeit entgangen, dass die Rechnung **{{rechnung.nummer}}** noch offen ist. Wir bitten Sie, den offenen Betrag innerhalb von **{{mahnung.frist_tage}} Tagen** (bis {{mahnung.frist_datum}}) zu begleichen. Für diese Erinnerung entstehen Ihnen keine zusätzlichen Kosten.',
  1: 'trotz unserer Zahlungserinnerung konnten wir zu Rechnung **{{rechnung.nummer}}** keinen Zahlungseingang feststellen. Wir berechnen daher Mahngebühren in Höhe von **{{mahnung.gebuehr_prozent}} % der Nettosumme**. Bitte begleichen Sie den Gesamtbetrag innerhalb von **{{mahnung.frist_tage}} Tagen** (bis {{mahnung.frist_datum}}).',
  2: 'auch auf unsere 1. Mahnung zu Rechnung **{{rechnung.nummer}}** ist keine Zahlung erfolgt. Es fallen erneut **{{mahnung.gebuehr_prozent}} % Mahngebühren** auf die Nettosumme an. Wir fordern Sie letztmalig auf, den Gesamtbetrag innerhalb von **{{mahnung.frist_tage}} Tagen** (bis {{mahnung.frist_datum}}) zu begleichen. Andernfalls übergeben wir die Forderung ohne weitere Ankündigung an ein Inkassounternehmen.',
  3: 'da trotz zweifacher Mahnung zu Rechnung **{{rechnung.nummer}}** kein Zahlungseingang erfolgt ist, wurde die Forderung inklusive aller angefallenen Mahngebühren zur weiteren Beitreibung an ein **Inkassounternehmen übergeben**. Bitte richten Sie Zahlungen und Rückfragen ab sofort an das beauftragte Inkassobüro.',
};
const mahnungFelder = (stufe) => [
  f('anrede', 'Anrede', ANREDE, { typ: 'text' }),
  f('text', 'Mahntext', MAHNUNG_TEXTE[stufe], { hilfe: '**fett** hervorheben; Platzhalter unten einfügen' }),
  f('schluss', 'Schlusssatz', 'Sollte sich Ihre Zahlung mit diesem Schreiben überschnitten haben, betrachten Sie es bitte als gegenstandslos.'),
  f('gruss', 'Grußformel', GRUSS, { typ: 'text' }),
];

export const VORLAGEN_FELDER = Object.freeze({
  angebot: [
    f('anrede', 'Anrede', ANREDE, { typ: 'text' }),
    f('einleitung', 'Einleitung', 'vielen Dank für Ihre Anfrage und das Interesse an unseren Leistungen.\nGerne unterbreiten wir Ihnen hiermit folgendes Angebot:', { legacy: 'greeting_offer' }),
    f('schluss', 'Schlusstext', 'Dieses Angebot ist 30 Tage gültig. Wir freuen uns auf Ihren Auftrag.', { legacy: 'closing_offer' }),
    f('gruss', 'Grußformel', GRUSS, { typ: 'text' }),
    f('unterzeichner', 'Unterzeichner', '{{firma.geschaeftsfuehrer}}', { typ: 'text' }),
    f('gueltigkeit_tage', 'Gültigkeit (Tage)', '30', { typ: 'number', hilfe: 'füllt {{angebot.gueltig_bis}}' }),
  ],
  bestellung: [
    f('anrede', 'Anrede', ANREDE, { typ: 'text' }),
    f('einleitung', 'Einleitung', 'vielen Dank für Ihre Bestellung. Wir bestätigen folgenden Auftrag:', { legacy: 'greeting_order' }),
    f('schluss', 'Schlusstext', 'Vielen Dank für Ihr Vertrauen.', { legacy: 'closing_order' }),
    f('gruss', 'Grußformel', GRUSS, { typ: 'text' }),
    f('unterzeichner', 'Unterzeichner', '{{firma.geschaeftsfuehrer}}', { typ: 'text' }),
    f('mindestbestellwert', 'Mindestbestellwert (€ netto)', '0', { typ: 'number', hilfe: '0 = keine Prüfung. Gilt für Agent-, Admin- und Portal-Bestellungen (Warenwert nach Kundenrabatt).' }),
    f('mindermengenzuschlag', 'Mindermengenzuschlag (€ netto)', '0', { typ: 'number', hilfe: 'wird als eigene Position angefügt, wenn der Warenwert unter dem Mindestbestellwert liegt' }),
    f('zuschlag_konto', 'Erlöskonto Zuschlag', '4412', { typ: 'text', hilfe: 'Kontenrahmen, Standard 4412 „Erlöse Mindermengenzuschlag 19 %“' }),
    f('zuschlag_text', 'Positionstext Zuschlag', 'Mindermengenzuschlag (Warenwert unter {{bestellung.mindestbestellwert}} € netto)', { typ: 'text' }),
  ],
  lieferschein: [
    f('untertitel', 'Untertitel', 'Warenbegleitpapier — für den Transport', { typ: 'text' }),
    f('einleitung', 'Einleitung (optional)', '', { legacy: 'greeting_lieferschein', hilfe: 'leer = keine Zeile' }),
    f('hinweis', 'Hinweis am Ende', 'Bitte prüfen Sie die Ware bei Empfang auf Vollständigkeit und Unversehrtheit. Beanstandungen sind umgehend zu melden.', { legacy: 'closing_lieferschein' }),
    f('unterschrift_links', 'Unterschrift links', 'Übergeben durch (Name + Unterschrift)', { typ: 'text' }),
    f('unterschrift_rechts', 'Unterschrift rechts', 'Empfangen durch (Name + Unterschrift)', { typ: 'text' }),
  ],
  kommissionierschein: [
    f('untertitel', 'Untertitel', 'Pickliste — Lagerort sortiert', { typ: 'text' }),
    f('unterschrift_links', 'Unterschrift links', 'Kommissioniert durch (Name + Unterschrift)', { typ: 'text' }),
    f('unterschrift_rechts', 'Unterschrift rechts', 'Datum · Uhrzeit', { typ: 'text' }),
  ],
  rechnung: [
    f('dank', 'Untertitel / Dank', 'Vielen Dank für Ihren Auftrag.', { typ: 'text', legacy: 'thanks_for_payment' }),
    f('zahlungsbedingungen', 'Zahlungsbedingungen (Zusatz)', 'Zahlungsfrist: 14 Tage netto.', { legacy: 'payment_terms_text' }),
    f('ueberweisungshinweis', 'Hinweis zur Überweisung', 'Bitte geben Sie bei Überweisung die Rechnungsnummer {{rechnung.nummer}} an.'),
    f('schluss', 'Schlusstext', 'Bei Fragen zur Rechnung stehen wir Ihnen gerne zur Verfügung.', { legacy: 'invoice_closing' }),
  ],
  gutschrift: [
    f('schluss', 'Schlusstext', 'Der Gutschriftbetrag wird mit der genannten Rechnung verrechnet bzw. erstattet.'),
  ],
  mahnung_0: mahnungFelder(0),
  mahnung_1: mahnungFelder(1),
  mahnung_2: mahnungFelder(2),
  mahnung_3: mahnungFelder(3),
});

const isObj = (v) => v && typeof v === 'object' && !Array.isArray(v);
const typGruppe = (typ) => (typ.startsWith('mahnung_') ? 'mahnung' : typ);

/** Vollständige Vorlage eines Typs (alle Felder gefüllt). */
export function getVorlage(settings, typ) {
  const felder = VORLAGEN_FELDER[typ];
  if (!felder) return {};
  const s = isObj(settings) ? settings : {};
  const eigene = isObj(s.vorlagen) && isObj(s.vorlagen[typ]) ? s.vorlagen[typ] : {};
  const out = {};
  for (const feld of felder) {
    const own = eigene[feld.key];
    if (typeof own === 'string') { out[feld.key] = own; continue; }
    const legacy = feld.legacy ? s[feld.legacy] : null;
    out[feld.key] = typeof legacy === 'string' && legacy !== '' ? legacy : feld.standard;
  }
  return out;
}

/** Platzhalter, die ein Typ anbietet (für Chips im Editor). */
export function platzhalterFuer(typ) {
  const keys = PLATZHALTER_JE_TYP[typGruppe(typ)] || gemeinsam;
  return keys.map((key) => ({ key, ...PLATZHALTER[key] }));
}

const PLATZHALTER_RE = /\{\{\s*([a-z0-9_.]+)\s*\}\}/gi;

/** Ersetzt {{key}} aus dem Kontext (flache Map); unbekannt → leer. Reiner Text. */
export function renderPlatzhalter(text, kontext = {}) {
  if (text == null) return '';
  return String(text).replace(PLATZHALTER_RE, (_, key) => {
    const v = kontext[key];
    return v == null ? '' : String(v);
  });
}

/** Text → sicheres HTML: Platzhalter, Escaping, **fett**, Zeilenumbrüche. */
export function textZuHtml(text, kontext = {}) {
  const plain = renderPlatzhalter(text, kontext);
  return escapeHtml(plain)
    .replace(/\*\*([^*\n]+)\*\*/g, '<b>$1</b>')
    .replace(/\r?\n/g, '<br>');
}

/** Neues Settings-Objekt mit gesetztem Vorlagenfeld (unveränderlich). */
export function setVorlagenFeld(settings, typ, key, value) {
  const felder = VORLAGEN_FELDER[typ];
  if (!felder || !felder.some((x) => x.key === key)) return settings;
  const vorlagen = isObj(settings?.vorlagen) ? settings.vorlagen : {};
  const eigene = isObj(vorlagen[typ]) ? vorlagen[typ] : {};
  return { ...settings, vorlagen: { ...vorlagen, [typ]: { ...eigene, [key]: String(value ?? '') } } };
}

/** Entfernt alle eigenen Werte eines Typs → Standard/alte Spalten gelten wieder. */
export function resetVorlage(settings, typ) {
  const vorlagen = isObj(settings?.vorlagen) ? settings.vorlagen : {};
  const rest = Object.fromEntries(Object.entries(vorlagen).filter(([k]) => k !== typ));
  return { ...settings, vorlagen: rest };
}

export function isVorlageAngepasst(settings, typ) {
  const vorlagen = isObj(settings?.vorlagen) ? settings.vorlagen : {};
  return Boolean(isObj(vorlagen[typ]) && Object.keys(vorlagen[typ]).length > 0);
}
